import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	MenuItem,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { VForm, VMaskTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import {
	AgencyTransferDilutionType,
	IAgreement,
	SinisterAgreementService,
	computeAgencyTransferDistribution,
} from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { globalRemoveMaskReal2 } from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	agreementId: string;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalDefineAgencyTransferPlan = ({ open, agreementId, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [agreement, setAgreement] = useState<IAgreement | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [step, setStep] = useState<"build" | "review">("build");
	const [valueInput, setValueInput] = useState("0,00");
	const [formKey, setFormKey] = useState(0);
	const [dilutionType, setDilutionType] = useState<AgencyTransferDilutionType>("equal");
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Busca o acordo completo ao abrir em vez de confiar no objeto recebido do chamador — a listagem de
	// Acordos (AgreementRowMenu) só traz os campos usados nas colunas da tabela, sem `installments`
	// preenchido, então usar o `agreement` da linha deixava a distribuição sempre vazia.
	useEffect(() => {
		if (!open) return;
		setStep("build");
		setValueInput("0,00");
		setFormKey((prev) => prev + 1);
		setDilutionType("equal");
		setIsLoading(true);
		SinisterAgreementService.getById(agreementId).then((result) => {
			if (!(result instanceof Error)) setAgreement(result);
			setIsLoading(false);
		});
	}, [open, agreementId]);

	const installments = useMemo(() => agreement?.installments ?? [], [agreement]);
	const isAgreementCanceled = agreement?.status === "broken";
	const maxTransferValue = agreement?.totalValue ?? 0;
	// Já existe plano/repasse pra esse acordo (planejado ou já confirmado) — a ação vira "redefinir":
	// avisamos que o plano anterior (ainda não confirmado) será substituído, mas o que já foi
	// confirmado (agencyTransferRegistration) fica intocado — ver filtro em computeAgencyTransferDistribution.
	const hasExistingPlan = installments.some((installment) => (installment.agencyTransferValue ?? 0) > 0 || !!installment.agencyTransferRegistration);

	const rawValue = useMemo(() => {
		const cleaned = globalRemoveMaskReal2(String(valueInput || "0"));
		const parsed = Number(cleaned);
		return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
	}, [valueInput]);

	// O repasse não pode ultrapassar o valor total do acordo — se o usuário informar mais, avisamos e
	// ajustamos a conta (distribuição e envio) pro limite, sem travar a digitação.
	const exceedsAgreementLimit = rawValue > maxTransferValue;
	const totalValue = exceedsAgreementLimit ? maxTransferValue : rawValue;

	const distribution = useMemo(
		() => computeAgencyTransferDistribution(installments, totalValue, dilutionType),
		[installments, totalValue, dilutionType]
	);

	const handleClose = () => {
		onClose();
	};

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const result = await SinisterAgreementService.setAgencyTransferPlan(agreementId, { totalValue, dilutionType });
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({
			type: "success",
			messageOrContent: hasExistingPlan ? "Repasse à imobiliária redefinido com sucesso!" : "Repasse à imobiliária definido com sucesso!",
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
			<LoadingComp isopen={isLoading || isSubmitting} />
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
					{step === "build" ? (hasExistingPlan ? "Redefinir Repasse à Imobiliária" : "Definir Repasse à Imobiliária") : "Espelho da Distribuição"}
				</Typography>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{!agreement ? null : isAgreementCanceled ? (
					<Alert severity="warning">Não é possível definir repasse para um acordo cancelado.</Alert>
				) : step === "build" ? (
					<Box display="flex" flexDirection="column" gap={2.5} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							Valor total a ser devolvido para a imobiliária <strong>{agreement.client.name}</strong>. O valor será diluído entre as
							parcelas do acordo.
						</Alert>

						{hasExistingPlan && (
							<Alert severity="warning">
								Este acordo já possui um repasse definido. Ao confirmar, os valores ainda não confirmados serão substituídos pela nova
								distribuição — parcelas com repasse já confirmado não serão alteradas.
							</Alert>
						)}

						<VForm
							key={formKey}
							onSubmit={() => {}}
							initialData={{ transferValue: valueInput }}
							onPointerEnterCapture={() => {}}
							onPointerLeaveCapture={() => {}}
							placeholder=""
						>
							<VMaskTextField
								fullWidth
								size="small"
								label="Valor a repassar"
								name="transferValue"
								mask="real"
								placeholder="0,00"
								onChange={(e: any) => setValueInput(e?.target?.value ?? "")}
								onBlur={(e: any) => setValueInput(e?.target?.value ?? "")}
								InputProps={{
									startAdornment: <InputAdornment position="start">R$</InputAdornment>,
								}}
							/>
						</VForm>

						{exceedsAgreementLimit && (
							<Alert severity="warning">
								O valor informado ultrapassa o valor total do acordo ({formatScaleValueBR(String(maxTransferValue))}). O repasse será
								ajustado para esse limite.
							</Alert>
						)}

						{installments.length > 1 && (
							<TextField
								select
								fullWidth
								size="small"
								label="Forma de diluição do repasse"
								value={dilutionType}
								onChange={(e) => setDilutionType(e.target.value as AgencyTransferDilutionType)}
							>
								<MenuItem value="equal">Igualmente entre as parcelas</MenuItem>
								<MenuItem value="fast">O mais rápido possível (parcela a parcela)</MenuItem>
							</TextField>
						)}
					</Box>
				) : (
					<Box display="flex" flexDirection="column" gap={2}>
						<Typography variant="body2" color="text.secondary">
							Confira como o valor de {formatScaleValueBR(String(totalValue))} ficará distribuído entre as parcelas antes de confirmar.
						</Typography>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Parcela</TableCell>
										<TableCell align="center">Vencimento</TableCell>
										<TableCell align="right">Valor da Parcela</TableCell>
										<TableCell align="right">Repasse Planejado</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{installments.map((installment) => {
										const isConfirmed = !!installment.agencyTransferRegistration;
										return (
											<TableRow key={installment.number}>
												<TableCell>{installment.number}</TableCell>
												<TableCell align="center">{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}</TableCell>
												<TableCell align="right">{formatScaleValueBR(String(installment.value))}</TableCell>
												<TableCell align="right">
													{isConfirmed ? (
														<Chip size="small" label="Já confirmado" color="success" variant="outlined" />
													) : (
														formatScaleValueBR(String(distribution.get(installment.number) ?? 0))
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{isAgreementCanceled ? (
					<Button onClick={handleClose}>Fechar</Button>
				) : step === "build" ? (
					<>
						<Button onClick={handleClose}>Cancelar</Button>
						<Button variant="contained" disabled={totalValue <= 0} onClick={() => setStep("review")}>
							Ver Distribuição
						</Button>
					</>
				) : (
					<>
						<Button onClick={() => setStep("build")}>Voltar</Button>
						<Button variant="contained" disabled={isSubmitting} onClick={handleConfirm}>
							{hasExistingPlan ? "Confirmar Redefinição" : "Confirmar Repasse"}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};
